<!-- AdminFaculties.vue -->
<script>
import {mapActions, mapGetters} from "vuex";
import sizeVue from "@/mixins/size.vue";
import axios from "axios";
import PeriodsPricesDialog from "~/src/components/PeriodsPricesDialog.vue";

export default {
  name: "AdminFaculties",
  components: {PeriodsPricesDialog},
  mixins: [sizeVue],

  data: () => ({
    canCreate: false,
    canUpdate: false,
    canDelete: false,
    isLoading: false,

    // Keep options for local sort/pagination, but no server watcher
    options: {page: 1, itemsPerPage: 100, sortBy: [], sortDesc: []},

    // Global quick search
    search: "",

    // Advanced filters (LOCAL)
    filters: {
      nameUz: "",
      nameRu: "",
      nameEn: "",
      educationTypeId: null,
      educationLanguageId: null,
    },
    showFilters: false,

    // Periods dialog
    periodDialog: false,
    periodItem: null,

    // Delete dialog
    deleteDialog: false,
    deleteTarget: null,
    deleting: false,

    // Prices
    prices: [],
    pricesLoading: false,
    pricesError: null,

    // Price-type chip filter inside modal
    typeFilter: "all",

    // NEW: source segmented filter for the main table
    // 'all' | 'GLOBAL' | 'LOCAL'
    schetFilter: "all",

  }),

  computed: {
    ...mapGetters("admin-facultys", ["GET_FACULTYS"]),
    items(vm) {
      return vm.GET_FACULTYS || [];
    },

    ...mapGetters("admin-education-types", ["GET_EDUCATION_TYPES_ALL"]),
    ...mapGetters("admin-education-languages", ["GET_EDUCATION_LANGUAGE_ALL"]),

    typeItems() {
      return (this.GET_EDUCATION_TYPES_ALL || []).map((t) => ({
        text: t.name,
        value: t.id,
      }));
    },
    langItems() {
      return (this.GET_EDUCATION_LANGUAGE_ALL || []).map((l) => ({
        text: l.name,
        value: l.id,
      }));
    },

    // Local filtering + search (+ NEW source filter)
    filteredItems() {
      const norm = (v) =>
          (v ?? "")
              .toString()
              .toLowerCase()
              .normalize("NFD")
              .replace(/\p{Diacritic}/gu, "")
              .trim();

      const qRaw = (this.search || "").toString();
      const q = norm(qRaw); // normalized
      const qCompact = q.replace(/[^a-z0-9]/gi, ""); // hyphen/space-insensitive

      const f = {
        uz: norm(this.filters.nameUz),
        ru: norm(this.filters.nameRu),
        en: norm(this.filters.nameEn),
        typeId: this.filters.educationTypeId,
        langId: this.filters.educationLanguageId,
      };

      const want = this.schetFilter; // 'all' | 'GLOBAL' | 'LOCAL'

      return (this.items || []).filter((it) => {
        const n = it?.names || {};
        const uz = norm(n.uz);
        const ru = norm(n.ru);
        const en = norm(n.en);

        // include mvdir in search
        const mvdir = norm(it?.mvdir);
        const mvdirCompact = (it?.mvdir ?? "")
            .toString()
            .toLowerCase()
            .replace(/[^a-z0-9]/gi, "");

        // --- NEW: source filtering based on schetType (GLOBAL => GLOBAL, LOCAL => LOCAL)
        if (want !== "all") {
          const tag = this._sourceTagFromSchetType(it?.schetType);
          if (want === "GLOBAL" && tag !== "GLOBAL") return false;
          if (want === "LOCAL" && tag !== "LOCAL") return false;
        }

        if (f.uz && !uz.includes(f.uz)) return false;
        if (f.ru && !ru.includes(f.ru)) return false;
        if (f.en && !en.includes(f.en)) return false;

        if (f.typeId != null && it?.educationType?.id !== f.typeId) return false;
        if (f.langId != null && it?.educationLanguage?.id !== f.langId) return false;

        if (q) {
          const hay = `${uz} ${ru} ${en} ${mvdir}`;
          const hayCompact = hay.replace(/[^a-z0-9]/gi, "");

          const match =
              hay.includes(q) ||
              (qCompact &&
                  (hayCompact.includes(qCompact) || mvdirCompact.includes(qCompact)));
          if (!match) return false;
        }
        return true;
      });
    },

    filteredCount() {
      return this.filteredItems.length;
    },

    // Convenient getters for the dialog table
    periodRows() {
      const it = this.periodItem || {};
      return [
        {
          label: this.$t("Kunduzgi"),
          bak: this._num(it.periodKunduzgi),
          mag: this._num(it.periodKunduzgiMaster),
        },
        {
          label: this.$t("Kechki"),
          bak: this._num(it.periodKechki),
          mag: this._num(it.periodKechkiMaster),
        },
        {
          label: this.$t("Sirtqi"),
          bak: this._num(it.periodSirtqi),
          mag: this._num(it.periodSirtqiMaster),
        },
      ];
    },

    typeOrderMap() {
      return {1: 1, 2: 2, 3: 3};
    },
    typeLabelMap() {
      return {
        1: this.$t("Kunduzgi"),
        2: this.$t("Kechki"),
        3: this.$t("Sirtqi"),
      };
    },

    // Prices after filtering & sorting
    pricesView() {
      const order = this.typeOrderMap;
      const filter = this.typeFilter;

      const rank = (p) => {
        const id = Number(p.education_type_id);
        if (order[id]) return order[id];
        // Fallback by name (in case id missing)
        const nm = (p.education_type_name || "").toLowerCase();
        if (nm.includes("kunduz")) return 1;
        if (nm.includes("kech")) return 2;
        if (nm.includes("sirt")) return 3;
        return 999;
      };

      let arr = Array.isArray(this.prices) ? [...this.prices] : [];

      // filter by chip
      if (filter !== "all") {
        const wanted = Number(filter);
        arr = arr.filter((p) => Number(p.education_type_id) === wanted);
      }

      // sort by canonical order, then kurs (if exists), then id
      arr.sort((a, b) => {
        const ra = rank(a),
            rb = rank(b);
        if (ra !== rb) return ra - rb;
        const ka = Number(a.kurs ?? 0),
            kb = Number(b.kurs ?? 0);
        if (Number.isFinite(ka) && Number.isFinite(kb) && ka !== kb)
          return ka - kb;
        return Number(a.id || 0) - Number(b.id || 0);
      });

      return arr;
    },

    segStyle() {
      const base = { width: "calc(33.333% - 2px)", transform: "translateX(0)" };
      if (this.schetFilter === "GLOBAL") base.transform = "translateX(100%)";
      if (this.schetFilter === "LOCAL") base.transform = "translateX(200%)";
      return base;
    },
  },

  created() {
    this.INIT_EDUCATION_TYPES_ALL();
    this.INIT_EDUCATION_LANGUAGE_ALL();
  },

  async mounted() {
    this.canCreate = await this.hasRole("FAKULTETLAR", "CREATE");
    this.canUpdate = await this.hasRole("FAKULTETLAR", "UPDATE");
    this.canDelete = await this.hasRole("FAKULTETLAR", "DELETE");
    this.getItems();
  },

  methods: {
    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },

    ...mapActions("admin-facultys", ["INIT_FACULTYS"]),
    ...mapActions("admin-education-types", ["INIT_EDUCATION_TYPES_ALL"]),
    ...mapActions("admin-education-languages", ["INIT_EDUCATION_LANGUAGE_ALL"]),

    // Map various possible fields into a normalized "source tag"
    // GLOBAL => 'GLOBAL', LOCAL => 'LOCAL', otherwise null
    _sourceTagFromSchetType(v) {
      if (!v) return null;
      const s = String(v).toUpperCase();
      if (s === "GLOBAL") return "GLOBAL";
      if (s === "LOCAL") return "LOCAL";
      return null;
    },

    codesRows() {
      // Build one row per type in canonical order
      const addRow = (typeId, label, key) => {
        if (this.typeFilter !== "all" && Number(this.typeFilter) !== typeId)
          return null;

        const arr = Array.isArray(this.prices) ? this.prices : [];

        // Prefer code from an item with the matching education_type_id
        let code =
            arr.find(
                (p) =>
                    Number(p.education_type_id) === typeId &&
                    (p[key] ?? "").toString().trim()
            )?.[key] ||
            arr
                .map((p) => (p[key] ?? "").toString().trim())
                .find(Boolean) ||
            null;

        code = (code || "").toString().trim() || null;

        return {typeId, label, code};
      };

      const rows = [];
      const r1 = addRow(1, this.$t("Kunduzgi"), "fcode_kunduzgi");
      if (r1) rows.push(r1);
      const r2 = addRow(2, this.$t("Kechki"), "fcode_kechki");
      if (r2) rows.push(r2);
      const r3 = addRow(3, this.$t("Sirtqi"), "fcode_sirtqi");
      if (r3) rows.push(r3);
      return rows;
    },

    // Builds labeled code chips per item, respecting typeFilter
    codesForItem(p) {
      const out = [];
      const add = (key, label) => {
        const direct = (p[key] ?? "").toString().trim();
        const fallback = (p.fcode ?? "").toString().trim(); // optional
        const value = direct || fallback;
        if (value) out.push({key, label, value});
      };

      const tf = this.typeFilter === "all" ? null : Number(this.typeFilter);
      if (!tf || tf === 1) add("fcode_kunduzgi", this.$t("Kunduzgi"));
      if (!tf || tf === 2) add("fcode_kechki", this.$t("Kechki"));
      if (!tf || tf === 3) add("fcode_sirtqi", this.$t("Sirtqi"));
      return out;
    },

    fmtSummN(v, {decimals = 0, useNarrowSpace = false} = {}) {
      if (v === null || v === undefined) return "—";
      const s = String(v).trim();
      if (!s) return "—";

      const digitsOnly = s.replace(/[^\d]/g, "");
      if (!digitsOnly) return "—";

      const n = Number(digitsOnly);
      if (!Number.isFinite(n)) return "—";

      const out = new Intl.NumberFormat("uz-UZ", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(n);

      const space = useNarrowSpace ? "\u202F" : " ";
      return out.replace(/\u00A0|\u202F/g, space);
    },

    typeRank(p) {
      const map = {1: 1, 2: 2, 3: 3};
      const id = Number(p.education_type_id);
      if (map[id]) return map[id];
      const nm = (p.education_type_name || "").toLowerCase();
      if (nm.includes("kunduz")) return 1;
      if (nm.includes("kech")) return 2;
      if (nm.includes("sirt")) return 3;
      return 999;
    },

    typeLabel(p) {
      const id = Number(p.education_type_id);
      if (this.typeLabelMap[id]) return this.typeLabelMap[id];
      return p.education_type_name || this.$t("Type");
    },

    // Helpers
    fmtBool(v) {
      return v ? "✓" : "—";
    },
    clip(v, n = 64) {
      if (v == null) return "—";
      const s = String(v);
      return s.length > n ? s.slice(0, n) + "…" : s;
    },

    async loadPrices(id) {
      if (!id) return;
      this.pricesLoading = true;
      this.pricesError = null;
      this.prices = [];
      try {
        const http = this.$axios || (await import("axios")).default;
        const {data} = await http.get(`/admin/faculty/get/${id}/prices`);
        this.prices = Array.isArray(data) ? data : [];
      } catch (e) {
        this.pricesError =
            e?.response?.data?.message || e?.message || "Failed to load prices";
      } finally {
        this.pricesLoading = false;
        this.typeFilter = "all";
      }
    },

    async getItems() {
      if (this.isLoading) return;
      this.isLoading = true;
      try {
        await this.INIT_FACULTYS({
          page: 1,
          itemsPerPage: 100000,
          sortBy: [],
          sortDesc: [],
        });
      } finally {
        this.isLoading = false;
      }
    },

    rowNumber(index) {
      const page = this.options?.page || 1;
      const per = this.options?.itemsPerPage || 100;
      return (page - 1) * per + index + 1;
    },

    toggleFilters() {
      this.showFilters = !this.showFilters;
    },

    resetFilters() {
      this.filters = {
        nameUz: "",
        nameRu: "",
        nameEn: "",
        educationTypeId: null,
        educationLanguageId: null,
      };
    },

    // --- Periods modal ---
    onRowClick(item) {
      this.periodItem = item;
      if (this.periodDialog) {
        // already open, force reload
        this.$refs.periodsDialog.reload();
      } else {
        this.periodDialog = true;
      }
      this.loadPrices(item?.id);
    },

    _num(v) {
      if (v === null || v === undefined || v === "") return "—";
      const n = Number(v);
      return Number.isFinite(n) ? n : v;
    },

    // --- Visibility toggle ---
    async toggleVisibility(item) {
      try {
        const http = this.$axios || axios;
        await http.post(`/admin/faculty/${item.id}/toggle-visibility`);
        item.visibility = !item.visibility;
      } catch (err) {
        console.error("toggleVisibility error:", err);
      }
    },

    // --- Delete flow ---
    askDelete(item, e) {
      if (e && e.stopPropagation) e.stopPropagation();
      this.deleteTarget = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      try {
        const id = this.deleteTarget.id;
        const http = this.$axios || axios;
        await http.delete(`/admin/faculty/delete`, {params: {id}});

        const idx = this.items.findIndex((i) => i.id === id);
        if (idx !== -1) this.items.splice(idx, 1);

        this.$store.dispatch("SNACK", {
          color: "success",
          text: this.$t("Deleted successfully"),
        });
      } catch (err) {
        this.$store.dispatch("SNACK", {
          color: "error",
          text: err?.response?.data?.message || this.$t("Delete failed"),
        });
      } finally {
        this.deleting = false;
        this.deleteDialog = false;
        this.deleteTarget = null;
      }
    },

  },
};
</script>

<template>
  <v-container fluid class="af-container">
    <v-card class="af-card">
      <!-- Top bar -->
      <div class="af-toolbar">
        <div class="af-title-wrap">
          <div class="af-title">{{ $t('Faculties') }}</div>
          <div class="af-subtitle">
            {{ filteredCount }} {{ $t('items') }}
          </div>
        </div>

        <div class="af-actions">
          <v-btn class="af-btn" @click="toggleFilters" :title="$t('Filters')">
            <v-icon>mdi-tune-variant</v-icon>
          </v-btn>

          <v-text-field
              :placeholder="$t('Izlash...')"
              append-icon="mdi-magnify"
              v-model="search"
              dense
              hide-details
              outlined
              clearable
              class="search-input"
              style="max-width: 320px;"
          />

          <div class="seg">
            <button class="seg__btn" :class="{ 'is-active': schetFilter==='all' }" @click="schetFilter='all'">Hammasi</button>
            <button class="seg__btn" :class="{ 'is-active': schetFilter==='GLOBAL' }" @click="schetFilter='GLOBAL'">GLOBAL</button>
            <button class="seg__btn" :class="{ 'is-active': schetFilter==='LOCAL' }" @click="schetFilter='LOCAL'">LOCAL</button>
            <span class="seg__fill" :style="segStyle"></span>
          </div>
        </div>
      </div>

      <!-- Filters (LOCAL) -->
      <v-expand-transition>
        <div v-show="showFilters" class="af-filters">
          <div class="af-filters-grid">
            <v-text-field
                v-model="filters.nameUz"
                dense
                outlined
                hide-details
                clearable
                :label="$t('Name uz')"
                class="af-filter-input"
            />
            <v-text-field
                v-model="filters.nameRu"
                dense
                outlined
                hide-details
                clearable
                :label="$t('Name ru')"
                class="af-filter-input"
            />
            <v-text-field
                v-model="filters.nameEn"
                dense
                outlined
                hide-details
                clearable
                :label="$t('Name en')"
                class="af-filter-input"
            />
          </div>

          <div class="af-filters-actions">
            <v-btn small text class="af-reset" @click="resetFilters">
              <v-icon left small>mdi-restore</v-icon>
              {{ $t('Reset filters') }}
            </v-btn>
          </div>
        </div>
      </v-expand-transition>

      <div class="af-divider"></div>

      <!-- Loading bar -->
      <div class="af-loading" v-show="isLoading">
        <div class="af-loading-bar"></div>
      </div>

      <v-data-table
          :mobile-breakpoint="0"
          fixed-header
          :headers="[
          { text: '№', value: 'row-number', width: 72, sortable: false },
          { text: $t('Mvdir'), value: 'mvdir' },
          { text: $t('Type'), value: 'names.schetType' },
          { text: $t('Name uz'), value: 'names.uz' },
          { text: $t('Name ru'), value: 'names.ru' },
          { text: $t('Name en'), value: 'names.en' },
          { text: $t('Actions'), value: 'actions', sortable: false, align: 'end', width: 96 },
        ]"
          :items="filteredItems"
          :options.sync="options"
          item-key="id"
          :items-per-page="100"
          :footer-props="{ itemsPerPageOptions: [100], showFirstLastPage: true }"
          class="af-table elevation-0"
          :loading="false"
      >
        <!-- Skeleton while first load -->
        <template v-slot:body="props">
          <tbody v-if="isLoading" class="af-skeleton-body">
          <tr v-for="n in 8" :key="'sk-' + n" class="af-skel-row">
            <td>
              <div class="sk sk-num"></div>
            </td>
            <td>
              <div class="sk sk-text"></div>
            </td>
            <td>
              <div class="sk sk-text"></div>
            </td>
            <td>
              <div class="sk sk-text"></div>
            </td>
            <td>
              <div class="sk sk-text"></div>
            </td>
            <td>
              <div class="sk sk-text"></div>
            </td>
          </tr>
          </tbody>

          <transition-group v-else tag="tbody" name="fade-slide" class="af-tbody">
            <tr
                v-for="(item, index) in props.items"
                :key="item.id"
                class="af-row"
                style="cursor:pointer"
                @click="onRowClick(item)"
            >
              <td class="af-cell-num">{{ rowNumber(index) }}</td>
              <td class="af-cell-text" :title="item.mvdir">
                <span class="mono">{{ item.mvdir }}</span>
              </td>
              <td style="display: flex; align-items: center; justify-content: center">
                <template v-if="_sourceTagFromSchetType(item?.schetType)">
                  <span
                      class="src-badge"
                      :class="{
                      'src-badge--global': _sourceTagFromSchetType(item?.schetType)==='GLOBAL',
                      'src-badge--local':  _sourceTagFromSchetType(item?.schetType)==='LOCAL'
                    }"
                      :title="_sourceTagFromSchetType(item?.schetType)"
                      aria-label="Schet type"
                  >
                    {{ _sourceTagFromSchetType(item?.schetType) }}
                  </span>
                </template>

              </td>


              <td class="af-cell-text" :title="item.names?.uz">
                {{ item.names?.uz }}
              </td>
              <td class="af-cell-text" :title="item.names?.ru">
                {{ item.names?.ru }}
              </td>
              <td class="af-cell-text" :title="item.names?.en">
                {{ item.names?.en }}
              </td>

              <!-- Actions -->
              <td class="text-right pr-4" @click.stop>
                <div class="tc-actions" style="justify-content:flex-end">
                  <button class="tc-act tc-act--visibility" :title="item.visibility ? 'Yashirish' : 'Ko\'rsatish'" @click="toggleVisibility(item)">
                    <v-icon size="18" :color="item.visibility ? '#16a34a' : '#9ca3af'">
                      {{ item.visibility ? 'mdi-eye' : 'mdi-eye-off' }}
                    </v-icon>
                  </button>
                  <button v-if="canDelete" class="tc-act tc-act--delete" :title="$t('Delete')" @click="askDelete(item, $event)">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="!props.items.length && !isLoading" key="no-data">
              <td :colspan="6" class="text-center py-6">
                {{ $t('No data') }}
              </td>
            </tr>
          </transition-group>
        </template>
      </v-data-table>
    </v-card>

    <periods-prices-dialog
        ref="periodsDialog"
        :periodDialog="periodDialog"
        :periodItem="periodItem"
        @update:periodDialog="periodDialog = $event"
        @saved="reloadFaculties()"
    />

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog" max-width="420" eager scrollable>
      <v-card class="del-classic" elevation="0">
        <div class="delc__header">
          <div class="delc__title">{{ $t('Delete Faculty?') }}</div>
          <v-btn
              icon
              small
              class="delc__close"
              @click="deleteDialog=false"
              :disabled="deleting"
              aria-label="Close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="delc__body">
          <div class="delc__subtitle">
            {{ $t('This action cannot be undone.') }}
          </div>

          <div class="delc__target" v-if="deleteTarget">
            <div class="delc__name">{{ deleteTarget?.names?.uz }}</div>
            <div class="delc__mvdir">#{{ deleteTarget?.mvdir }}</div>
          </div>
        </div>

        <div class="delc__actions">
          <v-btn class="delc__btn delc__btn--ghost" text :disabled="deleting" @click="deleteDialog=false">
            {{ $t('Cancel') }}
          </v-btn>

          <v-btn class="delc__btn delc__btn--danger" depressed :loading="deleting" @click="confirmDelete">
            <v-icon left small>mdi-delete-outline</v-icon>
            {{ $t('Delete') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style lang="scss" scoped>
/* --- Theme tokens (light) --- */
$af-bg: #ffffff;
$af-card: rgba(255, 255, 255, 0.9);
$af-border: rgba(0, 30, 60, 0.08);
$af-shadow: 0 10px 30px rgba(10, 30, 60, 0.06);
$af-shadow-hover: 0 14px 40px rgba(10, 30, 60, 0.1);
$af-text: #0f172a;
$af-muted: #64748b;
$af-accent: #2563eb;
$af-accent-2: #60a5fa;


/* Accent helpers (for rgba) */
$af-accent-press: #1d4ed8;
$af-accent-contrast: #ffffff;
$af-accent-color: rgb(37, 99, 235);
$af-accent-04: rgba($af-accent-color, .04);
$af-accent-06: rgba($af-accent-color, .06);
$af-accent-08: rgba($af-accent-color, .08);
$af-accent-12: rgba($af-accent-color, .12);
$af-accent-shadow: 0 4px 10px rgba($af-accent-color, .25);

/* Delete dialog tokens */
$del-radius: 12px;
$del-border: #E5E7EB;
$del-text: #111827;
$del-muted: #6B7280;
$del-danger: #DC2626;
$del-danger-press: #B91C1C;
$del-bg: #FFFFFF;

/* Page container */
.af-container {
  background: $af-bg;
  padding-top: 12px;
}

/* Card */
.af-card {
  border-radius: 18px;
  border: 1px solid $af-border;
  background: $af-card;
  backdrop-filter: saturate(120%) blur(8px);
  box-shadow: $af-shadow;
  overflow: visible;
  transition: box-shadow .25s ease, transform .25s ease;

  &:hover {
    box-shadow: $af-shadow-hover;
  }
}

/* Toolbar */
.af-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 18px 20px 12px;
}

.af-title-wrap {
  display: flex;
  flex-direction: column;
}

.af-title {
  font-weight: 700;
  font-size: 20px;
  letter-spacing: .2px;
  color: $af-text;
}

.af-subtitle {
  margin-top: 2px;
  font-size: 12px;
  color: $af-muted;
}

.af-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* ── Search Input ── */
.search-input {
  max-width: none;
}

::v-deep .search-input {
  .v-input__slot {
    background: #f1f5f9 !important;
    border-radius: 10px !important;
    min-height: 38px !important;
  }
  fieldset {
    border-color: transparent !important;
  }
  &:hover fieldset {
    border-color: #d0d5dd !important;
  }
  &.v-input--is-focused fieldset {
    border-color: #00274c !important;
    border-width: 1.5px !important;
  }
  .v-icon {
    color: #94a3b8 !important;
    font-size: 20px !important;
  }
  input {
    font-size: 14px;
  }
  input::placeholder {
    color: #94a3b8 !important;
  }
}

::v-deep .search-input.theme--dark {
  .v-input__slot {
    background: #22232e !important;
  }
  fieldset {
    border-color: transparent !important;
  }
  &:hover fieldset {
    border-color: #33354a !important;
  }
  &.v-input--is-focused fieldset {
    border-color: #ffd451 !important;
  }
  .v-icon {
    color: #606275 !important;
  }
  input {
    color: #d5d6de !important;
  }
  input::placeholder {
    color: #606275 !important;
  }
}

.af-btn {
  border-radius: 12px !important;
  background: #fff !important;
  border: 1px solid $af-border !important;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06) !important;
  transition: transform .15s ease, box-shadow .2s ease;
  min-width: 44px !important;
  width: 44px !important;
  height: 44px !important;
  padding: 0 !important;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(15, 23, 42, .10) !important;
  }

  &--spinning {
    .v-icon {
      animation: spin 1s linear infinite;
    }
  }
}

/* Segmented control (payments-style) */
.seg {
  position: relative;
  display: inline-grid;
  grid-template-columns: repeat(3, 1fr);
  background: #f1f5f9;
  border: 1px solid $af-border;
  border-radius: 999px;
  padding: 3px;
  height: 38px;
  align-items: center;
  flex-shrink: 0;
}

.seg__btn {
  z-index: 2;
  appearance: none;
  border: 0;
  background: transparent;
  padding: 6px 20px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 13px;
  color: #475569;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
  transition: color .15s;
}

.seg__btn.is-active {
  color: #000;
}

.seg__fill {
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 3px;
  width: calc(33.333% - 2px);
  border-radius: 999px;
  background: #ffd451;
  box-shadow: 0 1px 3px rgba(0,0,0,.1);
  transition: transform .2s cubic-bezier(.4,0,.2,1);
}

/* Filters */
.af-filters {
  padding: 14px 20px 6px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.9), rgba(250, 252, 255, 0.9));
  border-top: 1px dashed $af-border;
  border-bottom: 1px dashed $af-border;
}

.af-filters-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 10px;
}

.af-filter-input {
  grid-column: span 3;
}

@media (max-width: 1200px) {
  .af-filter-input {
    grid-column: span 4;
  }
}

@media (max-width: 820px) {
  .af-filter-input {
    grid-column: span 6;
  }
}

@media (max-width: 520px) {
  .af-filter-input {
    grid-column: span 12;
  }
}

.af-filters-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 6px;
}

.af-reset {
  color: $af-muted !important;
  text-transform: none !important;
}

/* Divider */
.af-divider {
  height: 3px;
  width: 100%;
  background: linear-gradient(90deg, $af-accent, $af-accent-2);
  opacity: .14;
}

/* Loading bar */
.af-loading {
  position: relative;
  height: 3px;
}

.af-loading-bar {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, $af-accent, $af-accent-2);
  width: 30%;
  animation: indeterminate 1.2s infinite ease-in-out;
  border-radius: 0 2px 2px 0;
}

/* Data table */
.af-table {
  > > > .v-data-table {
    overflow: visible;
  }

  > > > .v-data-table__wrapper {
    overflow-y: visible !important;
  }

  > > > .v-data-table-header {
    position: sticky;
    top: 0;
    z-index: 3;
    background: linear-gradient(180deg, #ffffff 75%, rgba(255, 255, 255, 0.85));
    backdrop-filter: blur(6px);
  }

  > > > th {
    font-weight: 700 !important;
    font-size: 12.5px !important;
    color: $af-muted !important;
    text-transform: uppercase;
    letter-spacing: .6px;
    border-bottom: 1px solid $af-border !important;
  }

  > > > td {
    border-bottom: 1px solid $af-border !important;
    color: $af-text;
    font-size: 14.5px;
  }
}

.af-tbody {
  .af-row {
    transition: background .2s ease, transform .18s ease, box-shadow .18s ease;

    &:hover {
      background: rgba(99, 102, 241, 0.045);
      transform: translateZ(0);
    }
  }
}

/* Cells */
.af-cell-num {
  width: 72px;
  text-align: center;
  color: $af-muted;
}

.af-cell-text {
  max-width: 460px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Skeleton */
.af-skeleton-body {
  .af-skel-row td {
    padding: 14px 12px;
  }
}

.sk {
  display: inline-block;
  background: linear-gradient(100deg, #eef2f7 20%, #f6f9ff 40%, #eef2f7 60%);
  background-size: 200% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
  border-radius: 8px;
}

.sk-num {
  width: 36px;
  height: 16px;
}

.sk-text {
  width: 70%;
  height: 14px;
}

/* Dialogs */
.dlg-card, .del-card {
  border-radius: 16px;
  overflow: hidden;
}

.dlg-header, .del-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid $af-border;
  font-weight: 700;
}

.dlg-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dlg-subtitle {
  padding: 10px 18px 0 18px;
  color: $af-muted;
  font-size: 13px;
}

.dlg-table {
  margin: 10px 18px 0 18px;
  border: 1px solid $af-border;
  border-radius: 12px;
  overflow: hidden;

  thead th {
    background: #f8fafc;
  }

  td, th {
    border-bottom: 1px solid rgba(0, 0, 0, .06);
  }
}

.num {
  font-variant-numeric: tabular-nums;
}

.dlg-actions {
  display: flex;
  justify-content: flex-end;
  padding: 12px 18px 16px;
  border-top: 1px solid $af-border;
}

.del-body {
  padding: 14px 18px 6px;
}

.del-target {
  display: flex;
  gap: 10px;
  font-size: 14px;
  margin-bottom: 6px;

  .label {
    color: $af-muted;
    min-width: 120px;
  }
}

/* Transitions & keyframes */
.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all .22s ease;
}

.fade-slide-enter, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes indeterminate {
  0% {
    left: -10%;
    width: 20%;
  }
  50% {
    left: 40%;
    width: 35%;
  }
  100% {
    left: 100%;
    width: 20%;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes appear {
  from {
    opacity: 0;
    transform: scale(.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.dlg-section {
  padding: 10px 18px 0 18px;
}

.prices-table {
  margin-top: 8px;
  border: 1px solid $af-border;
  border-radius: 12px;
  overflow: hidden;

  thead th {
    background: #f8fafc;
  }
}

.mono {
  font-variant-numeric: tabular-nums;
}

.prices-loading {
  .sk-line {
    height: 12px;
    border-radius: 8px;
    margin: 8px 0;
    background: linear-gradient(100deg, #eef2f7 20%, #f6f9ff 40%, #eef2f7 60%);
    background-size: 200% 100%;
    animation: shimmer 1.2s ease-in-out infinite;
  }
}

/* Classic minimal delete dialog */
.del-classic {
  background: $del-bg;
  color: $del-text;
  border: 1px solid $del-border;
  border-radius: $del-radius;
  padding: 16px 16px 12px;
}

.delc__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.delc__title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: .2px;
}

.delc__close {
  color: #9CA3AF !important;
}

.delc__body {
  padding: 4px 4px 8px;
}

.delc__subtitle {
  font-size: 13px;
  color: $del-muted;
  margin-bottom: 12px;
}

.delc__target {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid $del-border;
  border-radius: 8px;
  background: #F9FAFB;
}

.delc__name {
  font-weight: 600;
  font-size: 14px;
}

.delc__mvdir {
  font-size: 12px;
  color: $del-muted;
}

.delc__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
}

.delc__btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: .1px;
  height: 36px !important;
  min-width: 88px;
}

.delc__btn--ghost {
  color: #374151 !important;
}

.delc__btn--danger {
  background: $del-danger !important;
  color: #fff !important;

  &:active, &:focus {
    background: $del-danger-press !important;
  }
}

@media (max-width: 480px) {
  .del-classic {
    padding: 14px 12px 10px;
  }
}

/* Price type chips */
.type-chips {
  margin-top: 8px;
}

.type-chip-group {
  :deep(.v-chip) {
    border-radius: 999px !important;
    transition: background .15s ease, box-shadow .15s ease, border-color .15s ease;
  }

  :deep(.v-chip.v-chip--outlined:hover) {
    background: $af-accent-06;
    border-color: $af-accent;
  }

  :deep(.v-chip .v-chip__content) {
    padding: 0 10px;
  }
}

.type-pill {
  display: inline-block;
  font-size: 12.5px;
  padding: 4px 8px;
  border-radius: 999px;
  background: $af-accent-08;
  border: 1px solid $af-border;
}

/* Aktiv holat: .v-chip va sizning modifier’ingiz birga */
.v-chip.type-chip--active {
  background-color: #ffd451 !important;
  color: #111827 !important;
  border-color: rgba(0, 30, 60, 0.12);

  .v-chip__content {
    color: inherit;
  }

  &:hover {
    background-color: #f5c842;
  }

  &:active {
    background-color: #facc15;
    transform: translateY(.5px) scale(.99);
  }

  &.v-chip--outlined,
  &.v-chip--no-color {
    background-color: #ffd451 !important;
  }
}

/* Ixtiyoriy: dark theme contrast */
.theme--dark .v-chip.type-chip--active {
  background-color: #d4a72e !important;
  color: #111827 !important;
}

/* Dark mode seg */
.theme--dark .seg { background: #22232e; border-color: rgba(255,255,255,.06); }
.theme--dark .seg__btn { color: #8b8d9e; }
.theme--dark .seg__btn.is-active { color: #000 !important; }

/* Source (GLOBAL/LOCAL) badge next to mvdir */
.src-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: .2px;
  line-height: 1;
  border: 1px solid $af-border;
  background: #fff;
  color: $af-muted;
  box-shadow: 0 2px 8px rgba(2, 6, 23, .06);
  vertical-align: middle;
  user-select: none;
}

.src-badge--global {
  background: $af-accent-08;
  border-color: $af-accent-12;
  color: $af-text;
}

.src-badge--local {
  background: #fde047; /* soft green */
  border-color: rgba(16, 185, 129, .22);
  color: #111827;
}

/* Dark theme tweaks */
.theme--dark .src-badge {
  background: rgba(255, 255, 255, .06);
  border-color: rgba(255, 255, 255, .10);
  color: rgba(255, 255, 255, .85);
}

.theme--dark .src-badge--global {
  background: rgba(37, 99, 235, .15);
  border-color: rgba(37, 99, 235, .35);
  color: rgba(255, 255, 255, .92);
}

.theme--dark .src-badge--local {
  background: rgba(16, 185, 129, .18);
  border-color: rgba(16, 185, 129, .35);
  color: rgba(255, 255, 255, .92);
}

/* Row action buttons */
.tc-actions { display: flex; gap: 6px; }
.tc-act {
  display: flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e5e7eb;
  background: none; color: #475569; cursor: pointer; transition: all .15s;
}
.tc-act:hover { border-color: #ffd451; color: #ffd451; background: rgba(255,212,81,.06); }
.tc-act--delete:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,.06); }

.theme--dark .tc-act { border-color: #2a2c3a; color: #9ca3af; }
.theme--dark .tc-act:hover { border-color: #ffd451; color: #ffd451; background: rgba(255,212,81,.06); }
.theme--dark .tc-act--delete:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,.06); }

</style>
